import type { BadgeDto, UserBadgesDto } from '@dijitalapp/shared';
import { MAX_BADGE_LOOKUP } from '@dijitalapp/shared';
import { api } from './api';

// People's badges, looked up in batches: every name on a screen asks, and the asks of one moment travel as
// one request. Answers are kept for a few minutes (badges change rarely), so lists scroll without asking again.
// Stale while revalidate: a badge on screen never disappears while it is being asked again (coming back to the
// app, a "badges changed" event, another language); the new answer replaces it when it arrives. The last
// answers are kept on this device with the chats (lib/offlineCache), so names show their badge at start.

const TTL_MS = 5 * 60_000;
const known = new Map<string, { at: number; badges: BadgeDto[] }>();
const waiting = new Set<string>();
/** Asked and not answered yet: not asked again meanwhile (a stale badge re-renders while its answer travels). */
const asking = new Set<string>();
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | null = null;
let locale = 'tr';
let version = 0;

function changed() {
  version += 1;
  listeners.forEach((l) => l());
}

/** Everything known is asked again on the next look (kept on screen meanwhile). */
function markStale(userId?: string) {
  if (userId) {
    const hit = known.get(userId);
    if (hit) known.set(userId, { at: 0, badges: hit.badges });
    return;
  }
  for (const [id, hit] of known) known.set(id, { at: 0, badges: hit.badges });
}

export function setBadgeLocale(next: string): void {
  if (next === locale) return;
  locale = next;
  // Names and meanings come in the new language with the next answer; the marks stay meanwhile.
  markStale();
  changed();
}

export function subscribeBadges(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}
export const badgesVersion = () => version;

/** After staff change a badge (or a while away): ask again, keeping what shows until the answer. */
export function forgetBadges(userId?: string): void {
  markStale(userId);
  changed();
}

/** Signing out or another account: nothing of the previous one stays. */
export function resetBadges(): void {
  known.clear();
  waiting.clear();
  asking.clear();
  if (timer) clearTimeout(timer);
  timer = null;
  changed();
}

/** What is worth keeping on this device: the answers of the language in use (staleness is decided on load). */
export function snapshotBadges(): { locale: string; badges: Record<string, BadgeDto[]> } {
  const badges: Record<string, BadgeDto[]> = {};
  for (const [id, hit] of known) badges[id] = hit.badges;
  return { locale, badges };
}

/**
 * The copy kept on this device, at start: shown at once, asked again on the first look (stale). Never
 * overwrites a fresher answer, and a copy in another language is not used.
 */
export function hydrateBadges(saved: { locale: string; badges: Record<string, BadgeDto[]> } | null | undefined): void {
  if (!saved || saved.locale !== locale) return;
  let added = false;
  for (const [id, badges] of Object.entries(saved.badges)) {
    if (known.has(id)) continue;
    known.set(id, { at: 0, badges });
    added = true;
  }
  if (added) changed();
}

async function flush() {
  timer = null;
  const ids = [...waiting].slice(0, MAX_BADGE_LOOKUP);
  ids.forEach((id) => {
    waiting.delete(id);
    asking.add(id);
  });
  if (waiting.size) timer = setTimeout(() => void flush(), 0);
  const asked = locale;
  const done = () => ids.forEach((id) => asking.delete(id));
  try {
    const found = await api<UserBadgesDto>('GET', `/users/badges?ids=${ids.join(',')}&locale=${asked}`).finally(done);
    // The language changed meanwhile: this answer is not kept (the new one is on its way).
    if (asked !== locale) return;
    const at = Date.now();
    let differs = false;
    for (const id of ids) {
      const next = found[id] ?? [];
      const prev = known.get(id)?.badges;
      // The same badges as before keep the same array: nothing on screen renders again.
      if (prev && sameBadges(prev, next)) known.set(id, { at, badges: prev });
      else {
        known.set(id, { at, badges: next });
        differs = true;
      }
    }
    if (!differs) return;
  } catch {
    // Not now (offline): asked again on the next look.
    return;
  }
  changed();
}

export function sameBadges(a: BadgeDto[], b: BadgeDto[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => {
    const y = b[i]!;
    return x.slug === y.slug && x.name === y.name && x.color === y.color && x.icon === y.icon && x.description === y.description;
  });
}

/** The person's badges if known (also while asking again), or [] the very first time (the name never waits). */
export function badgesFor(userId: string): BadgeDto[] {
  const hit = known.get(userId);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.badges;
  if (!waiting.has(userId) && !asking.has(userId)) {
    waiting.add(userId);
    timer ??= setTimeout(() => void flush(), 30);
  }
  return hit?.badges ?? EMPTY;
}

const EMPTY: BadgeDto[] = [];
